import type { Router, RouteRecordRaw } from 'vue-router'
import { ref } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { getLoginRoute, getCodeFromUrl, getSourceFromUrl, isAdmin, isDevFullMenu } from '@/utils/app-config'
import { cacheLoginRedirect, clearAllAuthParams } from '@utils/url'
import { filterRoutesByPermission, debugRouteFilter } from '@/utils/route-permission'
import { applyRateEntryPermissions } from '@/views/saas/utils/rate-entry-permission'
import { applyReportCenterRouteVisibility } from '@/views/report-center/transport-consignment/utils/report-route-permission'
import { generateMenuFromRoutes, flattenMenu, hasHomePermission } from '@/utils/menu'
import { adminRoutes } from './routes'
import { ensureShokzRoutes } from '@/router/shokz-routes-register'
import { loadShokzFeature } from '@/composables/useShokzFeature'
import { loadUnifiedOrderVersionConfig } from '@/composables/useUnifiedOrderVersion'
import { loadInquiryUnifiedVersionConfig } from '@/composables/useInquiryUnifiedVersion'
import { getTokenManager, TokenManager } from '@utils/token'
import { buildAnonymousAccessToken } from '@api/auth'
import { UserType } from '@/enums/auth'
import i18n, { t as globalT } from '@/i18n'
import { getTitleI18nKey } from '@/constants/title-i18n-map'
import { buildDocumentTitle } from '@/stores/system-brand'

// 过滤后路由（内部使用）
const filteredRoutes = ref<RouteRecordRaw[]>([])

/**
 * 计算登录后的默认落点：首个有权菜单（按菜单 order 升序，CCTOWER-2867）。
 * 无首页权限时跳过 /home；全无有权菜单返回 null，由调用方跳"暂无权限访问"页。
 */
function getFirstAccessiblePath(): string | null {
  const layout = filteredRoutes.value.find(r => r.name === 'Layout')
  if (!layout?.children?.length) {
    return null
  }
  const flat = flattenMenu(generateMenuFromRoutes(layout.children))
    .filter(m => m.path)
    .sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER))
  for (const item of flat) {
    if (item.path === '/home' && !hasHomePermission()) {
      continue
    }
    return item.path!
  }
  return null
}

/** 非韶音租户隐藏 EHR 专属路由 */
function filterOutShokzOnlyRoutes(routes: RouteRecordRaw[]): RouteRecordRaw[] {
  return routes
    .map(route => {
      if (route.meta?.requiresShokz) {
        return null
      }
      if (route.children?.length) {
        const children = filterOutShokzOnlyRoutes(route.children)
        if (children.length === 0 && route.meta?.requiresShokzParent) {
          return null
        }
        return { ...route, children }
      }
      return route
    })
    .filter((route): route is RouteRecordRaw => route !== null)
}

/**
 * 清除所有动态添加的路由
 * 保留基础路由（Root, Auth, SSO, NotFound）
 */
function clearDynamicRoutes(router: Router) {
  const currentRoutes = router.getRoutes()

  // 基础路由名称列表（这些路由不应该被删除）
  const baseRouteNames = [
    'Root',
    'Auth',
    'SSO',
    'NotFound',
    'SSOAuth',
    'TenantAuth',
    'StaffAuth',
    'PortalAuth',
    'SupplierSpaceBooking',
    'ExternalConsignmentConfirm',
    'SupplierBid',
    'SupplierQuotationH5',
    'BaseLayout',
    'Home',
    'NoPermission',
    // router/index.ts baseRoutes（勿在 loadPermissions 时被 removeRoute 清掉）
    'AnonymousAccess',
    'QuotationDetail',
    'QuotationExpressDetail',
    'ShokzQuotationDetail',
    'SalesQuotationLinkDetail',
    'TrackingQueryStandalone',
    'TrackingQueryStandalonePage',
    'ShokzInquiryBpmApproval',
    'ShokzInquiryBpmApprovalByBusiness',
    'TcFeeBpmApproval',
    'SettlementBillBpmApproval',
    'SpecialPriceBpmApproval',
    'SpecialPriceApprovalDetail',
    // 费用更改审批详情是基础路由，权限加载时不能被清理，否则首页“处理”无法匹配路由。
    'TransportConsignmentFeeChangeBpmApproval'
  ]

  currentRoutes.forEach(route => {
    // 只删除动态添加的路由，保留基础路由
    if (route.name && !baseRouteNames.includes(String(route.name))) {
      router.removeRoute(route.name)
    }
  })
}

/**
 * 加载用户权限并动态添加路由（dev-token 登录后需先调用，避免跳转业务页被拦到登录页）
 */
export async function loadPermissionsAndRoutes(router: Router): Promise<void> {
  const authStore = useAuthStore()

  // 检查权限是否已加载，避免重复加载
  if (authStore.permissionsLoaded) {
    return
  }

  try {
    // 先清除之前的所有动态路由
    clearDynamicRoutes(router)

    // 从store加载权限
    const userPermissions = await authStore.loadUserPermissions()
    const isShokzTenant = isDevFullMenu() ? true : await loadShokzFeature()
    await loadUnifiedOrderVersionConfig()
    await loadInquiryUnifiedVersionConfig()

    // 根据应用类型选择路由配置并添加新路由
    let routes: RouteRecordRaw[]
    if (isDevFullMenu()) {
      console.warn('[CT_AUTH] VITE_DEV_FULL_MENU=true，已跳过菜单权限过滤，展示全量路由')
      routes = adminRoutes
    } else {
      routes = filterRoutesByPermission(adminRoutes, userPermissions)
      if (!isShokzTenant) {
        routes = filterOutShokzOnlyRoutes(routes)
      }
    }
    // 局部补充报告中心和运价入口准入，其余业务沿用既有菜单过滤。
    routes = applyRateEntryPermissions(applyReportCenterRouteVisibility(routes))
    filteredRoutes.value = routes

    // 动态添加过滤后的路由
    routes.forEach(route => {
      router.addRoute(route)
    })

    // 韶音 MDM 路由动态注册（避免必须先打开企业管理才能访问 mdm-customer-vendor）
    ensureShokzRoutes(router)

    // 标记路由已添加（通过store管理状态）
    authStore.setRoutesAdded(true)
  } catch (error) {
    console.error('权限加载失败:', error)
    throw error
  }
}

/**
 * 设置路由守卫
 */
export function setupRouterGuards(router: Router) {
  // 全局前置守卫 - 统一处理所有路由逻辑
  router.beforeEach(async(to, from, next) => {
    const authStore = useAuthStore()

    // 方案 A：redirect 会在 afterEach(clearAllAuthParams) 被清理，所以先缓存到 sessionStorage
    if (to.path.startsWith('/auth')) {
      const redirect = to.query?.redirect as string | undefined
      if (redirect) {
        cacheLoginRedirect(redirect)
      }
    }

    // 设置页面标题（支持国际化）
    if (to.meta.title) {
      const rawTitle = String(to.meta.title)
      const titleEn = to.meta.titleEn ? String(to.meta.titleEn) : undefined
      const i18nKey = getTitleI18nKey(rawTitle)
      const localizedTitle = i18n.global.locale.value === 'en-US' && titleEn
        ? titleEn
        : globalT(i18nKey)
      document.title = buildDocumentTitle(localizedTitle)
    }

    // 检测并设置来源标识
    const source = getSourceFromUrl()
    if (source) {
      authStore.setSourceFrom(source)
    }

    // 检测URL中的code参数，如果存在且用户未登录则自动登录
    const code = getCodeFromUrl()
    if (code && !authStore.isAuthenticated) {
      try {
        const result = await authStore.autoLoginWithCode(code)

        // 检查登录结果，如果返回null说明token获取失败
        if (!result) {
          next(getLoginRoute())
          return
        }

        // 🔧 自动登录成功后，需要获取权限并添加动态路由
        if (!authStore.permissionsLoaded) {
          try {
            await loadPermissionsAndRoutes(router)
          } catch (error) {
            next(getLoginRoute())
            return
          }
        }

        // 继续原路由或跳转到默认落点（首个有权菜单，CCTOWER-2867）
        if (to.path === getLoginRoute()) {
          next(getFirstAccessiblePath() ?? '/no-permission')
        } else {
          // 重新触发导航，确保动态路由生效
          next({ path: to.fullPath, replace: true })
        }
        return
      } catch (error) {
        next(getLoginRoute())
        return
      }
    }

    // 不需要认证且没有权限要求的页面直接放行
    if (to.meta.requiresAuth) {
      next()
      return
    }

    const isTransportConsignmentExternalRoute = to.path.startsWith('/external/consignment/')
      || to.path.startsWith('/supplier/space-booking/')
    const shouldCreateAnonymousToken = to.path.startsWith('/anonymous-access')
      || to.path.startsWith('/external')
      || to.path.startsWith('/bidding/supplier/bid')
      || to.path.startsWith('/supplier-quotation')
      || isTransportConsignmentExternalRoute

    // 匿名页面进入业务组件前先获取 token，避免首个详情请求无鉴权。
    if (shouldCreateAnonymousToken) {
      try {
        // 守卫执行时 URL 可能尚未提交，运输委托外链需显式选择匿名 Cookie。
        const tokenManager = isTransportConsignmentExternalRoute
          ? new TokenManager('ct_anonymous-access_token')
          : getTokenManager()
        // 从URL参数中获取userType和tenantId
        const userType = UserType.CLIENT

        // 调用API生成匿名访问token
        const token = await buildAnonymousAccessToken({ userType })

        // 设置token到cookie
        tokenManager.set(token, 1)
      } catch (error) {
        console.error('[CT_AUTH] 创建匿名访问token失败:', error)
      }
    }

    // 基础路由（不需要认证和权限的路由）直接放行
    const baseRoutePaths = [
      '/auth',
      '/sso',
      '/404',
      '/anonymous-access',
      '/supplier',
      '/external',
      '/bidding/supplier/bid',
      '/tracking-query',
      '/no-permission',
      '/supplier-quotation'
    ]
    if (baseRoutePaths.some(path => to.path.startsWith(path))) {
      next()
      return
    }

    // 检查用户是否已登录
    if (!authStore.isAuthenticated) {
      next({
        path: getLoginRoute(),
        query: { redirect: to.fullPath }
      })
      return
    }

    // 权限未加载，先获取权限并添加动态路由
    if (!authStore.permissionsLoaded) {
      try {
        // 加载权限和路由
        await loadPermissionsAndRoutes(router)
        // 在动态添加路由后，必须重新触发导航
        // 先阻止当前导航
        next(false)

        // 然后通过 router.replace 重新导航，确保查询参数不丢失
        await router.replace({
          path: to.path,
          query: to.query,
          hash: to.hash
        })
        return
      } catch (error) {
        console.error('权限加载失败:', error)
        next(getLoginRoute())
        return
      }
    }

    // 登录后默认落点：首个有权菜单；全无有权菜单则跳"暂无权限访问"页（CCTOWER-2867）
    if (to.path === '/') {
      return next({ path: getFirstAccessiblePath() ?? '/no-permission', replace: true })
    }

    // 无首页权限时拦截 /home（首页路由不带 meta.permission，需单独判断）
    if (to.path === '/home' && !hasHomePermission()) {
      return next({ path: getFirstAccessiblePath() ?? '/no-permission', replace: true })
    }

    // 4. 权限已加载，检查路由权限（开发全量菜单时与 loadPermissionsAndRoutes 一致，跳过校验）
    if (to.meta?.permission && isAdmin() && !isDevFullMenu()) {
      const hasRoutePermission = authStore.hasPermission(to.meta.permission as string)
      if (!hasRoutePermission) {
        next('/404')
        return
      }
    }

    next()
  })

  // 全局后置守卫
  router.afterEach(_to => {
    clearAllAuthParams()
  })

  // 全局解析守卫
  router.beforeResolve(async(to, from, next) => {
    // 在导航被确认之前，同时在所有组件内守卫和异步路由组件被解析之后
    next()
  })

  // 页面刷新时重置权限状态（确保每次都重新获取权限）
  window.addEventListener('beforeunload', () => {
    const authStore = useAuthStore()
    authStore.resetPermissions()
  })
}
